import { Pass, FullScreenQuad } from "three/examples/jsm/postprocessing/Pass.js";
import * as THREE from "three";
import debug from "debug";

const log = debug("app:sobel-zoom");

/**
 * Sobel Edge Detection Pass - depth (silhouette) only.
 */
export class SobelEdgePass extends Pass {
  constructor(resolution = new THREE.Vector2(256, 256)) {
    super();

    this.resolution = resolution;
    this.enabled = true;
    /** When set, use this depth texture instead of readBuffer.depthTexture (e.g. models-only, excludes bbox) */
    this.externalDepthTexture = null;
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: null },
        tDepth: { value: null },
        resolution: { value: this.resolution },
        edgeColor: { value: new THREE.Color(0x000000) },
        edgeStrength: { value: 1.0 },
        edgeThreshold: { value: 0.02 },
        /** Min depth span (max-min in kernel) to allow edge; suppresses flat-surface / thin-part artifacts */
        minDepthSpan: { value: 0.012 },
        /** Edge opacity 0.1-0.9 from camera.zoom. Set from setZoomFactor. */
        edgeMultiplier: { value: 0.9 },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform sampler2D tDepth;
        uniform vec2 resolution;
        uniform vec3 edgeColor;
        uniform float edgeStrength;
        uniform float edgeThreshold;
        uniform float edgeMultiplier;
        uniform float minDepthSpan;

        varying vec2 vUv;

        void main() {
          vec2 texel = vec2(1.0 / resolution.x, 1.0 / resolution.y);
          float stepLo = edgeThreshold - 0.03;
          float stepHi = edgeThreshold + 0.03;

          // 5x5 depth span - requires depth variation over larger area; filters localized thin-part artifacts
          float minD = 1.0, maxD = 0.0;
          for (int i = -2; i <= 2; i++) {
            for (int j = -2; j <= 2; j++) {
              float d = texture2D(tDepth, vUv + texel * vec2(float(i), float(j))).r;
              minD = min(minD, d);
              maxD = max(maxD, d);
            }
          }
          float depthSpan = maxD - minD;
          float spanGate = smoothstep(minDepthSpan, minDepthSpan + 0.008, depthSpan);
          spanGate = pow(spanGate, 1.5);  // Steepen: values just above threshold stay subdued

          // Depth edges (silhouette) - 3x3 blur of edge mask
          float edgeSum = 0.0;
          for (int di = -1; di <= 1; di++) {
            for (int dj = -1; dj <= 1; dj++) {
              vec2 off = texel * vec2(float(di), float(dj));
              float d00 = texture2D(tDepth, vUv + off + texel * vec2(-1.0, -1.0)).r;
              float d01 = texture2D(tDepth, vUv + off + texel * vec2(-1.0,  0.0)).r;
              float d02 = texture2D(tDepth, vUv + off + texel * vec2(-1.0,  1.0)).r;
              float d10 = texture2D(tDepth, vUv + off + texel * vec2( 0.0, -1.0)).r;
              float d12 = texture2D(tDepth, vUv + off + texel * vec2( 0.0,  1.0)).r;
              float d20 = texture2D(tDepth, vUv + off + texel * vec2( 1.0, -1.0)).r;
              float d21 = texture2D(tDepth, vUv + off + texel * vec2( 1.0,  0.0)).r;
              float d22 = texture2D(tDepth, vUv + off + texel * vec2( 1.0,  1.0)).r;
              float gx = -d00 - 2.0*d01 - d02 + d20 + 2.0*d21 + d22;
              float gy = -d00 - 2.0*d10 - d20 + d02 + 2.0*d12 + d22;
              float e = sqrt(gx*gx + gy*gy) * edgeStrength;
              edgeSum += smoothstep(stepLo, stepHi, e);
            }
          }
          float edge = (edgeSum / 9.0) * edgeMultiplier * spanGate;

          vec3 originalColor = texture2D(tDiffuse, vUv).rgb;
          vec3 finalColor = mix(originalColor, edgeColor, edge);

          gl_FragColor = vec4(finalColor, 1.0);
        }
      `,
    });

    this.fsQuad = new FullScreenQuad(this.material);
  }

  render(renderer, writeBuffer, readBuffer) {
    this.material.uniforms.tDiffuse.value = readBuffer.texture;
    this.material.uniforms.tDepth.value =
      this.externalDepthTexture ?? readBuffer.depthTexture;

    if (this.renderToScreen) {
      renderer.setRenderTarget(null);
      this.fsQuad.render(renderer);
    } else {
      renderer.setRenderTarget(writeBuffer);
      if (this.clear) renderer.clear();
      this.fsQuad.render(renderer);
    }
  }

  setSize(width, height) {
    this.resolution.set(width, height);
  }

  dispose() {
    this.material.dispose();
    this.fsQuad.dispose();
  }

  setEdgeColor(color) {
    this.material.uniforms.edgeColor.value.set(color);
  }

  setEdgeStrength(strength) {
    this.material.uniforms.edgeStrength.value = strength;
  }

  setEdgeThreshold(threshold) {
    this.material.uniforms.edgeThreshold.value = threshold;
  }

  /**
   * Min depth span (max-min in kernel) to allow edge. Higher = fewer edges (suppresses thin-part artifacts).
   * Default 0.004. Try 0.002–0.01 if tuning.
   */
  setMinDepthSpan(span) {
    this.material.uniforms.minDepthSpan.value = span;
  }

  /**
   * Use a custom depth texture for edge detection (e.g. models-only, excluding bounding boxes).
   * Pass null to use the default readBuffer depth.
   */
  setExternalDepthTexture(texture) {
    this.externalDepthTexture = texture;
  }

  /**
   * Set edge opacity (0.1-0.9) from camera.zoom. Very gentle transition.
   */
  setZoomFactor(cameraZoom) {
    const zoomMin = 0.125;
    const zoomMax = 4.0;    // Wide range = gradual transition
    const t = Math.max(0, Math.min(1, (cameraZoom - zoomMin) / (zoomMax - zoomMin)));
    const raw = Math.pow(t, 0.35);  // Very gentle curve - opacity changes slowly with zoom
    const mult = 0.1 + raw * 0.8;   // Remap to min 0.1, max 0.9 opacity
    const prev = this._lastMultiplier;
    if (prev !== mult) {
      log(`camera.zoom=${cameraZoom.toFixed(3)} → edgeMultiplier=${mult.toFixed(4)}`);
      this._lastMultiplier = mult;
    }
    this.material.uniforms.edgeMultiplier.value = mult;
    this.material.uniformsNeedUpdate = true;
    return mult;
  }
}
